<template>
  <section class="catalog-gallery" ref="root">
    <div class="catalog-gallery__inner" :class="{ 'catalog-gallery__inner_visible': inView }">
      <div v-if="dataLoading && !items.length" class="catalog-gallery__loading">
        <Loader size="lg" />
      </div>
      <template v-else>
      <!-- Табы по размеру и подсветке -->
      <div class="catalog-gallery__tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="catalog-gallery__tab"
          :class="{ 'catalog-gallery__tab_active': activeTab === tab.id }"
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- Секция с подписью размерности -->
      <div class="catalog-gallery__section" v-if="currentSection">
        <span class="catalog-gallery__section-label">Размер рамки</span>
        <h2 class="catalog-gallery__section-size">{{ currentSection.sizeLabel }}</h2>
        <p v-if="currentSection.desc" class="catalog-gallery__section-desc">{{ currentSection.desc }}</p>
      </div>

      <!-- Сетка работ -->
      <div class="catalog-gallery__grid">
        <div
          v-for="(item, i) in filteredItems"
          :key="item.id"
          class="catalog-gallery__card"
          :style="{ transitionDelay: inView ? `${0.04 + i * 0.05}s` : '0s' }"
        >
          <div class="catalog-gallery__img-wrap" @click="openLightbox(item)" role="button" tabindex="0" @keydown.enter.space.prevent="openLightbox(item)">
            <img
              :src="item.imageUrl"
              :alt="item.title"
              class="catalog-gallery__img"
              loading="lazy"
            />
            <span v-if="item.sell" class="catalog-gallery__sold">Продано</span>
          </div>
          <div class="catalog-gallery__meta">
            <span class="catalog-gallery__title">{{ item.title }}</span>
            <span v-if="item.description" class="catalog-gallery__desc">{{ item.description }}</span>
            <div v-if="priceByKey(item.sizeKey) && !item.sell" class="catalog-gallery__price-row">
              <span class="catalog-gallery__price">{{ formattedPriceByKey(item.sizeKey) }}</span>
              <button type="button" class="catalog-gallery__btn" @click="openOrderModal(item)">Купить</button>
            </div>
            <div v-else-if="item.sell" class="catalog-gallery__price-row catalog-gallery__price-row_sold">
              <span class="catalog-gallery__sold-label">Продано</span>
            </div>
          </div>
        </div>
      </div>
      </template>
    </div>

    <!-- Лайтбокс -->
    <Teleport to="body">
      <Transition name="lightbox">
        <div
          v-if="lightboxItem"
          class="catalog-gallery__lightbox"
          @click.self="closeLightbox"
          role="dialog"
          aria-modal="true"
          :aria-label="'Увеличить: ' + lightboxItem.title"
        >
          <button type="button" class="catalog-gallery__lightbox-close" aria-label="Закрыть" @click="closeLightbox">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <div class="catalog-gallery__lightbox-content" @click.self="closeLightbox">
            <div class="catalog-gallery__lightbox-img-wrap">
              <img :src="lightboxItem.imageUrl" :alt="lightboxItem.title" class="catalog-gallery__lightbox-img" />
              <span v-if="lightboxItem.sell" class="catalog-gallery__lightbox-sold">Продано</span>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </section>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { formatPrice } from '@/data/prices'
import Loader from '@/components/Loader.vue'

export default {
  name: 'CatalogGallery',
  components: { Loader },
  setup() {
    const store = useStore()
    const root = ref(null)
    const inView = ref(false)
    const activeTab = ref('all')
    const lightboxItem = ref(null)
    let observer = null

    const items = computed(() => store.getters['catalog/items'] || [])
    const priceRows = computed(() => store.getters['price/priceRows'] || [])
    const dataLoading = computed(() => store.getters['catalog/loading'] || store.getters['price/loading'])

    const tabs = computed(() => {
      const usedKeys = new Set(items.value.map((i) => i.sizeKey).filter(Boolean))
      const sizeTabs = priceRows.value
        .filter((r) => usedKeys.has(r.sizeKey))
        .map((r) => ({ id: r.sizeKey, label: r.sizeLabel, sizeKey: r.sizeKey }))

      const result = [{ id: 'all', label: 'Все', sizeKey: null }, ...sizeTabs]

      const hasBacklight = items.value.some((i) => i.category === 'backlight')
      if (hasBacklight) {
        result.push({ id: 'backlight', label: 'С подсветкой', sizeKey: '60x90' })
      }

      return result
    })

    const filteredItems = computed(() => {
      if (activeTab.value === 'all') return items.value
      if (activeTab.value === 'backlight') {
        return items.value.filter((item) => item.category === 'backlight')
      }
      // остальные табы — по sizeKey
      return items.value.filter((item) => item.sizeKey === activeTab.value)
    })

    const currentSection = computed(() => {
      if (activeTab.value === 'all') return null
      const t = tabs.value.find((tab) => tab.id === activeTab.value)
      if (!t || !t.sizeKey) return null
      const row = store.getters['price/priceByKey'](t.sizeKey)
      if (!row) return { sizeLabel: t.label, desc: null, price: null }
      return {
        sizeLabel: row.sizeLabel,
        desc: row.shortDescription || null,
        price: row.price ?? null
      }
    })

    function openLightbox(item) {
      lightboxItem.value = item
      document.body.style.overflow = 'hidden'
    }

    function closeLightbox() {
      lightboxItem.value = null
      document.body.style.overflow = ''
    }

    function openOrderModal(item) {
      store.commit('setOrderModalCatalogItem', item)
      store.commit('setOrderModalOpen', true)
    }

    function onKey(e) {
      if (e.key === 'Escape') closeLightbox()
    }

    onMounted(() => {
      store.dispatch('price/fetchPrices')
      store.dispatch('catalog/fetchItems')
      observer = new IntersectionObserver(
        ([e]) => { if (e.isIntersecting) inView.value = true },
        { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
      )
      if (root.value) observer.observe(root.value)
      window.addEventListener('keydown', onKey)
    })
    onUnmounted(() => {
      observer?.disconnect()
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    })

    const priceByKey = (key) => store.getters['price/priceByKey'](key)
    const formattedPriceByKey = (key) => store.getters['price/formattedPriceByKey'](key)

    return {
      root,
      inView,
      activeTab,
      lightboxItem,
      items,
      dataLoading,
      tabs,
      filteredItems,
      currentSection,
      priceByKey,
      formattedPriceByKey,
      formatPrice,
      openLightbox,
      closeLightbox,
      openOrderModal
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/_variables.scss";

$dur: 0.5s;
$ease: cubic-bezier(0.22, 1, 0.36, 1);

.catalog-gallery {
  padding: 48px $gap 100px;
  background: $color-bg;

  @media (max-width: $breakpoint-mobile) {
    padding: 32px $gap 72px;
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 280px;
    padding: 48px;
  }

  &__inner {
    max-width: $container-max;
    margin: 0 auto;

    &_visible .catalog-gallery__tab { opacity: 1; transform: translateY(0); }
    &_visible .catalog-gallery__section { opacity: 1; transform: translateY(0); }
    &_visible .catalog-gallery__card { opacity: 1; transform: translateY(0); }
  }

  &__tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 40px;
    border-bottom: 1px solid $color-border;
    padding-bottom: 20px;
  }

  &__tab {
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: $color-text-muted;
    background: none;
    border: 1px solid $color-border;
    border-radius: 8px;
    cursor: pointer;
    transition: color $dur $ease, border-color $dur $ease, background $dur $ease, opacity $dur $ease, transform $dur $ease;
    opacity: 0;
    transform: translateY(8px);

    &:hover {
      color: $color-text;
      border-color: $color-accent-teal;
    }

    &_active {
      color: $color-primary-inverse;
      background: $color-accent-teal;
      border-color: $color-accent-teal;
    }
  }

  &__section {
    margin-bottom: 36px;
    padding: 24px 28px;
    background: linear-gradient(135deg, rgba(13, 148, 136, 0.06) 0%, rgba(13, 148, 136, 0.02) 100%);
    border-left: 4px solid $color-accent-teal;
    border-radius: 0 12px 12px 0;
    opacity: 0;
    transform: translateY(12px);
    transition: opacity $dur $ease, transform $dur $ease;

    .catalog-gallery__inner_visible & {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &__section-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: $color-accent-teal;
    margin-bottom: 6px;
  }

  &__section-size {
    font-size: clamp(22px, 2.5vw, 28px);
    font-weight: 800;
    letter-spacing: -0.02em;
    color: $color-text;
    margin: 0 0 8px;
  }

  &__section-desc {
    font-size: 15px;
    line-height: 1.5;
    color: $color-text-muted;
    margin: 0;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: $gap;
  }

  &__card {
    display: flex;
    flex-direction: column;
    text-align: left;
    background: $color-primary-inverse;
    border: 1px solid $color-border;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
    opacity: 0;
    transform: translateY(20px);
    transition: opacity $dur $ease, transform $dur $ease, border-color $dur $ease, box-shadow $dur $ease;

    &:hover {
      border-color: $color-accent-teal;
      box-shadow: 0 12px 32px rgba(13, 148, 136, 0.12);
    }
    &:focus-visible {
      outline: 2px solid $color-accent-teal;
      outline-offset: 2px;
    }
  }

  &__img-wrap {
    position: relative;
    aspect-ratio: 4 / 3;
    overflow: hidden;
    background: $color-border;
    cursor: pointer;
  }

  &__sold {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
    padding: 10px 48px;
    background: rgba(178, 55, 55, 0.75);
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.12em;
    white-space: nowrap;
    pointer-events: none;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
  }

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &__meta {
    padding: 16px 20px;
    flex: 1;
  }

  &__title {
    display: block;
    font-size: 16px;
    font-weight: 600;
    color: $color-text;
    margin-bottom: 4px;
  }

  &__desc {
    display: block;
    font-size: 13px;
    line-height: 1.4;
    color: $color-text-muted;
  }

  &__price-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 10px;
    flex-wrap: wrap;

    &_sold {
      justify-content: flex-start;
    }
  }

  &__sold-label {
    font-size: 13px;
    font-weight: 600;
    color: $color-text-muted;
  }

  &__price {
    font-size: 17px;
    font-weight: 700;
    color: $color-accent-teal;
  }

  &__btn {
    flex-shrink: 0;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    color: $color-primary-inverse;
    background: $color-accent-teal;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.2s, opacity 0.2s;

    &:hover {
      background: darken($color-accent-teal, 8%);
    }
  }

  /* Lightbox (Teleport to body — наследуем шрифт явно) */
  &__lightbox {
    position: fixed;
    inset: 0;
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(0, 0, 0, 0.92);
    cursor: pointer;
    font-family: 'Arimo', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  &__lightbox-close {
    position: absolute;
    top: 24px;
    right: 24px;
    width: 48px;
    height: 48px;
    padding: 0;
    border: none;
    background: rgba(255, 255, 255, 0.1);
    color: $color-primary-inverse;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
    svg { width: 24px; height: 24px; }
  }

  &__lightbox-content {
    max-width: 90vw;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    cursor: default;
  }

  &__lightbox-img-wrap {
    position: relative;
    display: inline-block;
  }

  &__lightbox-sold {
    position: absolute;
    bottom: 12px;
    left: 50%;
    transform: translateX(-50%);
    padding: 6px 14px;
    background: rgba(0, 0, 0, 0.8);
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    border-radius: 6px;
    pointer-events: none;
  }

  &__lightbox-img {
    max-width: 100%;
    max-height: 85vh;
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 8px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  }
}

.lightbox-enter-active,
.lightbox-leave-active {
  transition: opacity 0.3s ease;
}
.lightbox-enter-from,
.lightbox-leave-to {
  opacity: 0;
}
.lightbox-enter-active .catalog-gallery__lightbox-content,
.lightbox-leave-active .catalog-gallery__lightbox-content {
  transition: transform 0.3s ease;
}
.lightbox-enter-from .catalog-gallery__lightbox-content,
.lightbox-leave-to .catalog-gallery__lightbox-content {
  transform: scale(0.95);
}
</style>
